import React from 'react';
import { Box, Button, Card, Chip, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';

const fmtDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

export default function ProspectUnderwritingProgress({ progress, onDismiss, onCancel, cancelling = false }) {
  const filename = typeof progress?.filename === 'string' ? progress.filename.trim() : '';
  const totalCompanies = Number(progress?.totalCompanies || 0);
  // Never render a placeholder/legacy job. The progress card represents one
  // concrete uploaded file, so it must have both a filename and file rows.
  if (!progress || !filename || totalCompanies <= 0 || progress?.status === 'idle') return null;
  const status = progress?.status || 'idle';
  const terminal = ['completed', 'failed', 'cancelled'].includes(status);
  const title = status === 'processing_companies'
    ? 'Reading and adding uploaded companies'
    : status === 'underwriting'
      ? 'AI underwriting is in progress'
      : status === 'completed'
        ? 'Latest upload underwriting completed'
        : status === 'failed'
          ? 'Latest upload needs attention'
          : status === 'cancelled'
            ? 'Upload stopped'
          : 'No prospect upload is currently running';
  const tone = status === 'failed' ? '#D14343' : status === 'completed' ? '#159947' : status === 'cancelled' ? '#718096' : '#5186EC';

  return (
    <Card
      variant="outlined"
      sx={{
        mt: 2,
        mb: 0.5,
        px: 2.5,
        py: 2,
        borderRadius: 2,
        borderColor: '#DCE5F3',
        boxShadow: '0 2px 8px rgba(34,57,93,.08)',
        background: 'linear-gradient(90deg, #F8FBFF 0%, #FFFFFF 65%)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
          {status === 'failed'
            ? <ErrorOutlineIcon sx={{ color: tone, mt: 0.2 }} />
            : status === 'completed'
              ? <CheckCircleOutlineIcon sx={{ color: tone, mt: 0.2 }} />
              : status === 'cancelled'
                ? <StopCircleOutlinedIcon sx={{ color: tone, mt: 0.2 }} />
              : <AutoAwesomeIcon sx={{ color: tone, mt: 0.2 }} />}
          <Box>
            <Typography sx={{ color: '#23395D', fontWeight: 700, fontSize: 16 }}>Prospect upload progress</Typography>
            <Typography sx={{ color: '#415574', fontSize: 13, mt: 0.25 }}>{title}</Typography>
            <Typography sx={{ color: '#718096', fontSize: 11.5, mt: 0.4 }}>
              {filename}{progress.createdAt ? ` · started ${fmtDateTime(progress.createdAt)}` : ''}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {!terminal && onCancel && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<StopCircleOutlinedIcon />}
              disabled={cancelling}
              onClick={onCancel}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {cancelling ? 'Stopping…' : 'Stop upload'}
            </Button>
          )}
          <Chip
            size="small"
            label={status === 'completed' ? 'Complete' : status === 'failed' ? 'Failed' : status === 'cancelled' ? 'Stopped' : `${progress.overallPercent || 0}% complete`}
            sx={{ bgcolor: `${tone}16`, color: tone, fontWeight: 700 }}
          />
          {onDismiss && (
            <Tooltip title="Close progress">
              <IconButton size="small" aria-label="Close upload progress" onClick={onDismiss}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <LinearProgress
        variant={status === 'underwriting' && !progress.underwritingTotal ? 'indeterminate' : 'determinate'}
        value={progress.overallPercent || 0}
        sx={{ mt: 1.5, height: 7, borderRadius: 5, bgcolor: '#E9EEF6', '& .MuiLinearProgress-bar': { bgcolor: tone, borderRadius: 5 } }}
      />
      <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 4 }, mt: 1.4, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12.5, color: '#415574' }}>
          <b>{progress.processedCompanies || 0}/{progress.totalCompanies || 0}</b> file rows processed
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: '#415574' }}>
          <b>{progress.underwritingCompleted || 0}/{progress.underwritingTotal || 0}</b> underwriting completed
        </Typography>
        {!terminal && (
          <Typography sx={{ fontSize: 12.5, color: '#415574' }}>
            <b>{progress.underwritingInProgress || 0}</b> active · <b>{progress.underwritingQueued || 0}</b> queued
          </Typography>
        )}
        {!!progress.errors && (
          <Typography sx={{ fontSize: 12.5, color: '#D14343' }}><b>{progress.errors}</b> upload errors</Typography>
        )}
      </Box>
      {progress.failureMessage && <Typography sx={{ mt: 1, color: '#D14343', fontSize: 12 }}>{progress.failureMessage}</Typography>}
    </Card>
  );
}
